import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { FaEye } from "react-icons/fa";
import { MdDelete, MdModeEditOutline } from "react-icons/md";
import { IoDuplicateOutline } from "react-icons/io5";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import ReactSelect from "react-select";
import { Modal } from "../../Components/Modal";
import MovieTile from "../../Components/MovieTile/MovieTile";
import Swal from "sweetalert2";
import Button from "../../Components/Button";
import SearchBar from "../../Components/SearchBar";
import { AsyncPaginate } from "react-select-async-paginate";
import { useTheme } from "../../context/ThemeContext";
import { RecentTilesSkeleton } from "../../Components/Skeleton";


const TILE_FIELDS = `
  tid,
  title,
  poster,
  portrait,
  longPortrait,
  background,
  square,
  banner,
  featureBanner,
  heroBanner,
  titleLogo,
  status,
  updated_at,
  updated_by,
  release_date,
  genre,
  languages,
  source,
  tags,
  imdb_id,
  year
`;

// Returns react-select inline styles that respect dark/light theme
const buildSelectStyles = (isDark, extra = {}) => ({
  menuPortal: (base) => ({ ...base, zIndex: 999 }),
  control: (base, state) => ({
    ...base,
    backgroundColor: isDark ? "#161b22" : "#fff",
    borderColor: state.isFocused
      ? "#3b82f6"
      : isDark ? "#30363d" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.2)" : "none",
    "&:hover": { borderColor: isDark ? "#484f58" : "#9ca3af" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: isDark ? "#161b22" : "#fff",
    border: `1px solid ${isDark ? "#30363d" : "#e5e7eb"}`,
    boxShadow: isDark
      ? "0 10px 25px rgba(0,0,0,0.5)"
      : "0 4px 6px rgba(0,0,0,0.1)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: isDark
      ? state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "rgba(59,130,246,0.15)"
        : "#161b22"
      : state.isSelected
      ? "#3b82f6"
      : state.isFocused
      ? "#eff6ff"
      : "#fff",
    color: isDark
      ? state.isSelected ? "#fff" : "#e6edf3"
      : state.isSelected ? "#fff" : "#111827",
    cursor: "pointer",
  }),
  singleValue: (base) => ({
    ...base,
    color: isDark ? "#e6edf3" : "#111827",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: isDark ? "rgba(59,130,246,0.2)" : "#dbeafe",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: isDark ? "#e6edf3" : "#1d4ed8",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: isDark ? "#8b949e" : "#6b7280",
    "&:hover": { backgroundColor: "#ef4444", color: "#fff" },
  }),
  placeholder: (base) => ({
    ...base,
    color: isDark ? "#8b949e" : "#9ca3af",
  }),
  input: (base) => ({
    ...base,
    color: isDark ? "#e6edf3" : "#111827",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: isDark ? "#30363d" : "#d1d5db",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: isDark ? "#8b949e" : "#6b7280",
    "&:hover": { color: isDark ? "#e6edf3" : "#374151" },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: isDark ? "#8b949e" : "#6b7280",
  }),
  ...extra,
});

const RecentTiles = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTile, setPreviewTile] = useState(null);

  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState([{ category: "", value: "" }]);
  const [tempFilters, setTempFilters] = useState([{ category: "", value: "" }]);
  const [uniqueCategoryValues, setUniqueCategoryValues] = useState({});
  const [allContentTypes, setAllContentTypes] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allImdbIds, setAllImdbIds] = useState([]);
  const [allTitles, setAllTitles] = useState([]);

  // Pagination state
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Ref for intersection observer
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // All related data maps
  const [allSource, setAllSource] = useState({});
  const [allGenres, setAllGenres] = useState({});
  const [allLanguages, setAllLanguages] = useState({});
  const [allCastMembers, setAllCastMembers] = useState({});
  const [allDirectors, setAllDirectors] = useState({});

  const minioBaseUrl = "https://images.do.cloudwalker.tv/images/";


  const PAGE_LIMIT = 10;

const loadFilterOptions = async (category, search, loadedOptions, { page }) => {
  let table = "";
  let labelKey = "";
  let valueKey = "id";
   // ✅ ADD THIS BLOCK (top priority)
  if (category === "synopsis") {
    const from = page * PAGE_LIMIT;
    const to = from + PAGE_LIMIT - 1;

    let query = supabase
      .from("tiles")
      .select("synopsis")
      .not("synopsis", "is", null)
      .range(from, to);

    if (search) {
      query = query.ilike("synopsis", `%${search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error(error);
      return { options: [], hasMore: false, additional: { page } };
    }

    // remove duplicates
    const unique = [
      ...new Map((data || []).map((item) => [item.synopsis, item])).values(),
    ];

    const options = unique.map((item) => ({
      value: item.synopsis,
      label: item.synopsis,
    }));

    return {
      options,
      hasMore: data.length === PAGE_LIMIT,
      additional: { page: page + 1 },
    };
  }

  switch (category) {
    case "languages":
      table = "languages";
      labelKey = "languages";
      break;
    case "source":
      table = "source";
      labelKey = "source";
      break;
    case "genre":
      table = "genres";
      labelKey = "genres";
      break;
    case "director":
      table = "directors";
      labelKey = "directors";
      break;
    case "cast":
      table = "castMembers";
      labelKey = "castMembers";
      break;
    case "contentType":
      table = "contentType";
      labelKey = "contentType";
      break;
      case "year":
  table = "year";
  labelKey = "year";
  break;
    default:
      return {
        options: [],
        hasMore: false,
        additional: { page: 0 }
      };
  }

  const from = page * PAGE_LIMIT;
  const to = from + PAGE_LIMIT - 1;

  let query = supabase
    .from(table)
    .select(`${valueKey}, ${labelKey}`)
    .range(from, to);

  if (search) {
    query = query.ilike(labelKey, `%${search}%`);
  }

  const { data, error } = await query;

  if (error) {
    console.error(error);
    return { options: [], hasMore: false, additional: { page } };
  }

  // const options = (data || []).map(item => ({
  //   value: item[valueKey],
  //   label: item[labelKey],
  // }));

  const options = (data || []).map(item => ({
  value: category === "year" ? String(item.year) : item[valueKey],
  label: String(item[labelKey]),
}));

  return {
    options,
    hasMore: data.length === PAGE_LIMIT,
    additional: {
      page: page + 1,
    },
  };
};

  // Category options for filters
  const category = [
    { key: "title", label: "Title" },
    { key: "languages", label: "Languages" },
    { key: "tags", label: "Tags" },
    { key: "imdb_id", label: "IMDB ID" },
    { key: "source", label: "Source" },
    { key: "genre", label: "Genre" },
    { key: "contentType", label: "ContentType" },
    { key: "year", label: "Year" },
    { key: "director", label: "Director" },
    { key: "cast", label: "Cast Members" },
    { key: "synopsis", label: "Synopsis" },
    { key: "status", label: "Status" },
  ];

  const STATUS_OPTIONS = [
    { value: "Published", label: "Published" },
    { value: "Unpublished", label: "Unpublished" },
    { value: "Universe", label: "Universe" },
    { value: "Draft Universe", label: "Draft Universe" },
  ];
const fetchAll = async (table, columns) => {
  let allData = [];
  let from = 0;
  const batchSize = 1000;

  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .range(from, from + batchSize - 1);

    if (error) {
      console.error(`Error fetching ${table}:`, error);
      break;
    }

    if (!data || data.length === 0) break;

    allData = [...allData, ...data];

    if (data.length < batchSize) break;

    from += batchSize;
  }

  console.log(`TOTAL ${table}:`, allData.length);

  return allData;
};
  // Fetch all necessary reference data


  const fetchByIds = async (table, column, ids) => {
  if (!ids.length) return [];

  const { data, error } = await supabase
    .from(table)
    .select(`id, ${column}`)
    .in("id", ids);

  if (error) {
    console.error(error);
    return [];
  }

  return data;
};


  // Fetch filter data when modal opens
  useEffect(() => {
    if (showFilterModal) {
      fetchFilterData();
      setTempFilters(JSON.parse(JSON.stringify(filters)));
    }
  }, [showFilterModal]);

  const fetchFilterData = async () => {
    try {
      const { data, error } = await supabase.from("tiles").select("*");
      if (error) throw error;

      const categoryValues = {};
      category.forEach(({ key }) => {
        categoryValues[key] = Array.from(
          new Set(data.map((item) => item[key]))
        ).filter(Boolean);
      });

      setUniqueCategoryValues(categoryValues);

      // Fetch tags
      const { data: tagsData } = await supabase
        .from("tiles")
        .select("tags")
        .not("tags", "is", null)
        .gt("tags", "[]")
        .limit(1000);

      if (tagsData) {
        let allTagsArray = [];
        tagsData.forEach(row => {
          try {
            const parsed = JSON.parse(row.tags);
            if (Array.isArray(parsed)) {
              allTagsArray.push(...parsed);
            }
          } catch (e) {
            const clean = row.tags.replace(/[\[\]"]/g, '').split(',').map(t => t.trim()).filter(Boolean);
            allTagsArray.push(...clean);
          }
        });
        setAllTags([...new Set(allTagsArray)]);
      }

      // Fetch IMDB IDs
      const { data: imdbData } = await supabase
        .from("tiles")
        .select("imdb_id")
        .not("imdb_id", "is", null)
        .neq("imdb_id", "");

      if (imdbData) {
        const imdbList = [...new Set(imdbData.map(x => x.imdb_id))];
        setAllImdbIds(imdbList);
      }

      // Fetch titles
      const { data: titleData } = await supabase
        .from("tiles")
        .select("id, title")
        .not("title", "is", null)
        .neq("title", "")
        .order("updated_at", { ascending: false });

      if (titleData) {
        const titleList = titleData.map(x => x.title).filter(Boolean);
        const uniqueTitles = [...new Set(titleList)];
        setAllTitles(uniqueTitles.map(t => ({ value: t, label: t })));
      }
    } catch (err) {
      console.error("Error fetching filter data:", err.message);
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch tiles from last week with pagination and filters
  const fetchRecentTiles = useCallback(async (pageNum = 0, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    const from = pageNum * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from("tiles")
      .select(TILE_FIELDS, { count: "exact" })
      .order("updated_at", { ascending: false })
      .range(from, to);

    // Apply filters
    const activeFilters = filters.filter(f => f.category && f.value && f.value.length > 0);

    activeFilters.forEach((filter) => {
      const { category: cat, value } = filter;
      if (!cat || !value || value.length === 0) return;

      const ids = value.map((v) => (v?.value !== undefined ? v.value : v));

      if (cat === "genre") {
        const matchValues = [];
        ids.forEach(v => {
          const lower = String(v).toLowerCase();
          matchValues.push(lower);
          Object.entries(allGenresRef.current).forEach(([uuid, name]) => {
            if (String(uuid).toLowerCase() === lower) matchValues.push(String(name).toLowerCase());
            if (String(name).toLowerCase() === lower) matchValues.push(String(uuid).toLowerCase());
          });
        });
        const unique = [...new Set(matchValues)];
        if (unique.length > 1) {
          const orString = unique.map(v => `genre.cs.{${v}}`).join(",");
          query = query.or(orString);
        } else {
          query = query.contains("genre", [unique[0]]);
        }
      } else if (cat === "languages") {
        const selected = value.map(v => v?.value ?? v);
        const matchValues = [];
        selected.forEach(v => {
          const lower = String(v).toLowerCase();
          matchValues.push(lower);
          Object.entries(allLanguagesRef.current).forEach(([uuid, name]) => {
            if (String(name).toLowerCase() === lower) matchValues.push(uuid.toLowerCase());
          });
          Object.entries(allLanguagesRef.current).forEach(([uuid, name]) => {
            if (String(uuid).toLowerCase() === lower) matchValues.push(String(name).toLowerCase());
          });
        });
        const unique = [...new Set(matchValues)];
        if (unique.length > 1) {
          const orString = unique.map(v => `languages.ilike.%${v}%`).join(",");
          query = query.or(orString);
        } else {
          query = query.ilike("languages", `%${unique[0]}%`);
        }
      }
      else if (cat === "source") {
        const selectedValues = value.map(v => v?.value ?? v);

        const matchValues = [];

        selectedValues.forEach(v => {
          const lower = String(v).toLowerCase();

          // uuid itself
          matchValues.push(lower);

          // find uuid by name
          Object.entries(allSourceRef.current).forEach(([uuid, name]) => {
            if (String(name).toLowerCase() === lower) {
              matchValues.push(uuid.toLowerCase());
            }
          });

          // find name by uuid
          Object.entries(allSourceRef.current).forEach(([uuid, name]) => {
            if (String(uuid).toLowerCase() === lower) {
              matchValues.push(String(name).toLowerCase());
            }
          });
        });

        const unique = [...new Set(matchValues)];

        const orString = unique
          .map(v => `source.cs.{${v}}`)
          .join(",");

        query = query.or(orString);
      }

      else if (cat === "contentType") {
        const matchValues = [];
        value.forEach(v => {
          const str = String(v?.value ?? v);
          matchValues.push(str);
          const found = allContentTypesRef.current.find(ct => ct.label === str);
          if (found) matchValues.push(found.value);
          const foundById = allContentTypesRef.current.find(ct => ct.value === str);
          if (foundById) matchValues.push(foundById.label);
        });
        const unique = [...new Set(matchValues)];
        if (unique.length > 1) {
          query = query.or(unique.map(v => `contentType.ilike.%${v}%`).join(","));
        } else {
          query = query.ilike("contentType", `%${unique[0]}%`);
        }
      } else if (cat === "cast") {
        const selected = value.map(v => v?.value ?? v);
        const matchValues = [];
        selected.forEach(v => {
          const lower = String(v).toLowerCase();
          matchValues.push(lower);
          Object.entries(allCastMembersRef.current).forEach(([uuid, name]) => {
            if (String(name).toLowerCase() === lower) matchValues.push(String(uuid).toLowerCase());
          });
          Object.entries(allCastMembersRef.current).forEach(([uuid, name]) => {
            if (String(uuid).toLowerCase() === lower) matchValues.push(String(name).toLowerCase());
          });
        });
        const unique = [...new Set(matchValues)];
        if (unique.length > 1) {
          const orString = unique.map(v => `cast.cs.{${v}}`).join(",");
          query = query.or(orString);
        } else {
          query = query.contains("cast", [unique[0]]);
        }
      } else if (cat === "director") {
        const selectedValues = value.map(v => v?.value ?? v);
        const matchValues = [];
        selectedValues.forEach(v => {
          const lower = String(v).toLowerCase();
          matchValues.push(lower);
          Object.entries(allDirectorsRef.current).forEach(([uuid, name]) => {
            if (String(uuid).toLowerCase() === lower) matchValues.push(String(name).toLowerCase());
          });
        });
        const unique = [...new Set(matchValues)];
        if (unique.length > 1) {
          const orString = unique.map(v => `director.cs.{${v}}`).join(",");
          query = query.or(orString);
        } else {
          query = query.contains("director", [unique[0]]);
        }
      } else if (cat === "tags") {
        const tags = ids.map((v) => v?.label ?? v);
        if (tags.length > 1) {
          query = query.or(tags.map((tag) => `tags.ilike.%${tag}%`).join(","));
        } else {
          query = query.ilike("tags", `%${tags[0]}%`);
        }
      } else if (cat === "status") {
        if (ids.length > 1) {
          const orString = ids.map((v) => `status.eq.${v}`).join(",");
          query = query.or(orString);
        } else {
          query = query.eq("status", ids[0]);
        }
      } else {
        if (ids.length > 1) {
          const orString = ids.map((v) => `${cat}.eq.${v}`).join(",");
          query = query.or(orString);
        } else {
          query = query.eq(cat, ids[0]);
        }
      }
    });

    // Add search filter if query exists
    if (debouncedSearchQuery) {
      query = query.ilike("title", `%${debouncedSearchQuery}%`);
    }

    const { data, error, count } = await query;

    // Extract IDs from tiles
    const genreIds = new Set();
    const langIds = new Set();
    const sourceIds = new Set();
    const castIds = new Set();
    const directorIds = new Set();

    (data || []).forEach(tile => {
      try {
        const g = typeof tile.genre === "string" ? JSON.parse(tile.genre) : tile.genre;
        g?.forEach(id => genreIds.add(id));
      } catch {}

      try {
        const l = typeof tile.languages === "string" ? JSON.parse(tile.languages) : tile.languages;
        l?.forEach(id => langIds.add(id));
      } catch {}

      try {
        const s = typeof tile.source === "string" ? JSON.parse(tile.source) : [tile.source];
        s?.forEach(id => sourceIds.add(id));
      } catch {}

      try {
        const c = typeof tile.cast === "string" ? JSON.parse(tile.cast) : tile.cast;
        c?.forEach(id => castIds.add(id));
      } catch {}

      try {
        const d = typeof tile.director === "string" ? JSON.parse(tile.director) : tile.director;
        d?.forEach(id => directorIds.add(id));
      } catch {}
    });

    // Fetch only required refs
    const [
      genresData,
      langsData,
      sourceData,
      castData,
      dirData
    ] = await Promise.all([
      fetchByIds("genres", "genres", [...genreIds]),
      fetchByIds("languages", "languages", [...langIds]),
      fetchByIds("source", "source", [...sourceIds]),
      fetchByIds("castMembers", "castMembers", [...castIds]),
      fetchByIds("directors", "directors", [...directorIds]),
    ]);

    // Merge into state
    setAllGenres(prev => ({ ...prev, ...Object.fromEntries(genresData.map(g => [g.id, g.genres])) }));
    setAllLanguages(prev => ({ ...prev, ...Object.fromEntries(langsData.map(l => [l.id, l.languages])) }));
    setAllSource(prev => ({ ...prev, ...Object.fromEntries(sourceData.map(s => [s.id, s.source])) }));
    setAllCastMembers(prev => ({ ...prev, ...Object.fromEntries(castData.map(c => [c.id, c.castMembers])) }));
    setAllDirectors(prev => ({ ...prev, ...Object.fromEntries(dirData.map(d => [d.id, d.directors])) }));

    if (error) {
      console.error("Error fetching recent tiles:", error);
    }

    if (isLoadMore) {
      setTiles(prev => [...prev, ...(data || [])]);
    } else {
      setTiles(data || []);
    }

    setTotalCount(count || 0);
    setHasMore(data?.length === PAGE_SIZE && (from + data.length) < count);
    setLoading(false);
    setLoadingMore(false);
  }, [debouncedSearchQuery, filters]);

  // Refs to hold latest state values
  const allGenresRef = useRef(allGenres);
  const allLanguagesRef = useRef(allLanguages);
  const allSourceRef = useRef(allSource);
  const allContentTypesRef = useRef(allContentTypes);
  const allCastMembersRef = useRef(allCastMembers);
  const allDirectorsRef = useRef(allDirectors);

  useEffect(() => {
    allGenresRef.current = allGenres;
  }, [allGenres]);

  useEffect(() => {
    allLanguagesRef.current = allLanguages;
  }, [allLanguages]);

  useEffect(() => {
    allSourceRef.current = allSource;
  }, [allSource]);

  useEffect(() => {
    allContentTypesRef.current = allContentTypes;
  }, [allContentTypes]);

  useEffect(() => {
    allCastMembersRef.current = allCastMembers;
  }, [allCastMembers]);

  useEffect(() => {
    allDirectorsRef.current = allDirectors;
  }, [allDirectors]);

  // Track if we're resetting to prevent double fetch
  const isResettingRef = useRef(false);

  // Reset to page 0 when search query or filters change
  useEffect(() => {
    if (page !== 0) {
      isResettingRef.current = true;
      setPage(0);
      setTiles([]);
    } else {
      // If already on page 0, just refetch
      fetchRecentTiles(0, false);
    }
  }, [debouncedSearchQuery, filters]);

  // Initial load and when search/filters/page change
  useEffect(() => {
    if (isResettingRef.current && page === 0) {
      isResettingRef.current = false;
      fetchRecentTiles(0, false);
    } else if (!isResettingRef.current) {
      fetchRecentTiles(page, page > 0);
    }
  }, [page]);

  // Refs for intersection observer state tracking
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const loadingRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];

        if (
          first.isIntersecting &&
          hasMoreRef.current &&
          !loadingMoreRef.current &&
          !loadingRef.current
        ) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "200px",
        threshold: 0
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [tiles]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter functions
  const addFilter = () => {
    setTempFilters([...tempFilters, { category: "", value: "" }]);
  };

  const removeFilter = (index) => {
    const updatedFilters = tempFilters.filter((_, i) => i !== index);
    setTempFilters(updatedFilters);
  };

  const handleFilterChange = (index, field, value) => {
    const updatedFilters = [...tempFilters];

    if (field === "value") {
      updatedFilters[index][field] = Array.isArray(value) ? value : [value];
      if (updatedFilters[index][field].length === 0) {
        updatedFilters.splice(index, 1);
      }
    }
    
    else {
      updatedFilters[index][field] = value;

        if (value === "tags") {
        updatedFilters[index].subFilterValues = Array.from(allTags).map(tag => ({
          value: tag,
          label: tag,
        }));
        updatedFilters[index].value = [];
        setTempFilters(updatedFilters);
        return;
      } else if (value === "status") {
        updatedFilters[index].subFilterValues = STATUS_OPTIONS.map((s) => ({
          value: s.value,
          label: s.label,
        }));
      } else if (value === "imdb_id") {
        updatedFilters[index].subFilterValues = allImdbIds
          .filter(v => v && v.trim() !== "")
          .map(v => ({ value: v, label: v }));
      }  else if (value === "title") {
        updatedFilters[index].subFilterValues = allTitles;
      } else {
        updatedFilters[index].subFilterValues = uniqueCategoryValues[value] || [];
        if (!updatedFilters[index].subFilterValues || updatedFilters[index].subFilterValues.length === 0) {
          updatedFilters.splice(index, 1);
        }
      }

      if (updatedFilters.length === 0) {
        updatedFilters.push({ category: "", value: [], subFilterValues: [] });
      }

      updatedFilters[index].value = [];
    }
    setTempFilters(updatedFilters);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setShowFilterModal(false);
  };

  const handleRemoveFilter = (cat, val) => {
    const updated = filters
      .map((f) =>
        f.category === cat
          ? {
            ...f,
            value: Array.isArray(f.value)
              ? f.value.filter((v) => (v.value || v) !== val)
              : [],
          }
          : f
      )
      .filter((f) => f.value && Array.isArray(f.value) && f.value.length > 0);
    setFilters(updated);
  };

  const handleClearAll = () => {
    setFilters(filters.map((f) => ({ ...f, value: [] })));
    setPage(0);
    setTiles([]);
  };

  const resolveNames = (value, map) => {
    if (!value) return [];
    let arr = [];
    try {
      arr = typeof value === "string" ? JSON.parse(value) : value;
    } catch {
      arr = Array.isArray(value) ? value : [value];
    }
    return arr.map(item => {
      const clean = item?.toString().trim().toLowerCase();
      const byName = Object.entries(map).find(
        ([id, name]) => name?.toString().trim().toLowerCase() === clean
      );
      if (byName) return byName[1];
      if (map[item]) return map[item];
      return item;
    });
  };

  // Helper function to extract image URL from a field
  const normalizePath = (p) => p.startsWith("tiles/") ? p : `tiles/${p}`;

  const extractImageUrl = (imageData) => {
    if (!imageData) return null;

    if (typeof imageData === "string") {
      if (imageData.startsWith("http")) return imageData;
      if (imageData.startsWith("[")) {
        try {
          const parsed = JSON.parse(imageData);
          const first = parsed[0];
          if (!first) return null;
          if (first.startsWith("http")) return first;
          return minioBaseUrl + normalizePath(first);
        } catch {
          return minioBaseUrl + normalizePath(imageData);
        }
      }
      return minioBaseUrl + normalizePath(imageData);
    }
    if (Array.isArray(imageData) && imageData.length > 0) {
      const first = imageData[0];
      if (typeof first === "string" && first.startsWith("http")) return first;
      return minioBaseUrl + normalizePath(first);
    }
    return null;
  };

  // Get image URL - poster || portrait || blank
  const getImageUrl = (tile) => {
    // Try poster first
    const posterUrl = extractImageUrl(tile.poster);
    if (posterUrl) return posterUrl;

    // Fallback to portrait
    const portraitUrl = extractImageUrl(tile.portrait);
    if (portraitUrl) return portraitUrl;

    // Fallback to blank
    return "";
  };

  // Handle preview
  const handlePreview = (tile) => {
    const resolvedTile = {
      ...tile,
      cast: resolveNames(tile.cast, allCastMembers),
      director: resolveNames(tile.director, allDirectors),
    };
    setPreviewTile([resolvedTile]);
    setShowPreview(true);
  };

  // Handle duplicate
  const handleDuplicate = async (tile) => {
    try {
      const { id, created_at, updated_at, ...clonedTile } = tile;
      const newTid = `TILE-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      clonedTile.tid = newTid;

      const { error } = await supabase.from("tiles").insert([clonedTile]);
      if (error) throw error;

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Tile duplicated successfully!",
        showConfirmButton: false,
        timer: 3000,
        toast: true,
        background: "white",
      });

      // Refresh tiles
      if (page === 0) {
        fetchRecentTiles(0, false);
      } else {
        setPage(0);
      }
    } catch (err) {
      console.error("Duplicate error:", err);
      Swal.fire("Error", err.message, "error");
    }
  };

  // Handle delete
  const handleDelete = async (tile) => {
    const result = await Swal.fire({
      title: "Delete Tile?",
      text: `Are you sure you want to delete "${tile.title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        // Delete images from storage
        const getRelativePath = (url) => {
          const baseUrl = "https://zigaeuvtypxeiwdraxca.supabase.co/storage/v1/object/public/files/";
          return url.replace(baseUrl, "");
        };

        const pathsToDelete = [tile.poster, tile.portrait, tile.background]
          .filter(Boolean)
          .map(getRelativePath)
          .filter(p => p);

        if (pathsToDelete.length > 0) {
          await supabase.storage.from("files").remove(pathsToDelete);
        }

        // Delete tile
        await supabase.from("tiles").delete().eq("tid", tile.tid);

        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Tile deleted!",
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          background: "white",
        });

        // Refresh
        if (page === 0) {
          fetchRecentTiles(0, false);
        } else {
          setPage(0);
        }
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire("Error", err.message, "error");
      }
    }
  };

  // Show info
  const showUpdateInfo = (tile) => {
    Swal.fire({
      title: "Tile Information",
      html: `
        <div style="text-align:left; font-size:14px">
          <p><b>Title:</b> ${tile.title || "N/A"}</p>
          <p><b>TID:</b> ${tile.tid || "N/A"}</p>
          <p><b>Created:</b> ${tile.created_at ? new Date(tile.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "N/A"}</p>
          <p><b>Updated:</b> ${tile.updated_at ? new Date(tile.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "N/A"}</p>
          <p><b>Updated By:</b> ${tile.updated_by || "N/A"}</p>
          <p><b>Status:</b> ${tile.status || "N/A"}</p>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Close",
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "Published": return "bg-green-500";
      case "Unpublished": return "bg-red-500";
      case "Universe": return "bg-purple-500";
      case "Draft Universe": return "bg-yellow-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="p-6 cw-bg min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold gradient-text">
          Recent Tiles
        </h1>
        <p className="text-gray-500 mt-2 text-md">Tiles released in the last 7 days</p>
        {totalCount > 0 && (
          <p className="text-gray-400 text-sm mt-1">Total: {totalCount} tiles</p>
        )}
      </div>

      {/* Filter Bar: Filter Button | Search Bar | Create Tile Button */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        {/* Filter Button */}
        <Button
          variant="primary"
          className="px-4 text-sm flex justify-between items-center gap-3"
          onClick={() => setShowFilterModal(true)}
        >
          <IoMdAdd />
          Filter
        </Button>

        {/* Clear Filter Icon */}
        {filters.some((f) => f.value && f.value.length > 0) && (
          <button
            onClick={() => {
              setFilters([{ category: "", value: "" }]);
              setPage(0);
              setTiles([]);
            }}
            title="Clear Filter"
            className="text-gray-500 hover:text-red-500"
          >
            <IoMdClose className="text-xl" />
          </button>
        )}

        {/* Search Bar */}
        <div className="flex-1 min-w-[200px]">
          <SearchBar
            placeholder="Search by title..."
            value={searchQuery}
            onChange={handleSearchChange}
            inputClassName="py-3"
          />
        </div>

        {/* Create Tile Button */}
        <Button
          variant="primary"
          onClick={() => navigate("/tile/create", { state: { fromRecentTiles: true } })}
          className="px-6 py-3 whitespace-nowrap"
        >
          Create Tile
        </Button>
      </div>

      {/* Filter Chips Display */}
      {filters.some((f) => f.value && f.value.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-semibold text-gray-700">Filters:</span>
          {filters.flatMap((f) =>
          
            (Array.isArray(f.value) ? f.value : [f.value])
              .filter((val) => val && val !== "select filters")
              .map((val, i) => {
                const id = val?.value || val;
                let displayValue = id;

                if (f.category === "languages") {
                  displayValue = allLanguages?.[id] || id;
                  console.log("Display value ==> ",displayValue)

                } else if (f.category === "source") {
                  displayValue = allSource?.[id] || id;
                } else if (f.category === "contentType") {
                  displayValue = allContentTypes.find((c) => c.value === id)?.label || id;
                } else if (f.category === "cast") {
                  displayValue = allCastMembers?.[id] || id;
                   console.log("Display value ==> ",displayValue)
        console.log("TOTAL cast:", Object.keys(allCastMembers).length);
                } else if (f.category === "director") {
                  displayValue = allDirectors?.[id] || id;
                } else if (f.category === "genre") {
                  displayValue = allGenres?.[id] || id;
        console.log("TOTAL genre:", Object.keys(allGenres).length);

                }
                else if (f.category === "year") {
  displayValue = id;
}

                return (
                  <div
                    key={`${f.category}-${id}-${i}`}
                    className="flex items-center gap-1 bg-gray-200 px-3 py-1 rounded-full text-sm text-gray-700"
                  >
                    <span>
                      {f.category}: {displayValue}
                    </span>
                    <button
                      onClick={() => handleRemoveFilter(f.category, id)}
                      className="ml-1 text-gray-600 hover:text-red-600"
                    >
                      ✕
                    </button>
                  </div>
                );
              })
          )}
          <button
            onClick={handleClearAll}
            className="ml-2 text-sm text-red-600 hover:underline"
          >
            Clear All
          </button>
        </div>
      )}

      {loading ? (
        <RecentTilesSkeleton count={8} />
      ) : tiles.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl p-16 text-center border border-gray-100">
          <div className="w-24 h-24 mx-auto mb-6 bg-[var(--surface-2)] rounded-full flex items-center justify-center shadow-inner">
            <span className="text-5xl">📋</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-700 mb-3">
            {debouncedSearchQuery || filters.some((f) => f.value && f.value.length > 0)
              ? "No Matching Tiles"
              : "No Recent Tiles"}
          </h3>
          <p className="text-gray-500 mb-8 text-lg">
            {debouncedSearchQuery
              ? `No tiles found matching "${debouncedSearchQuery}"`
              : filters.some((f) => f.value && f.value.length > 0)
                ? "No tiles match the selected filters"
                : "No tiles were created in the last 7 days."}
          </p>
          {!debouncedSearchQuery && !filters.some((f) => f.value && f.value.length > 0) && (
            <Button
              variant="primary"
              onClick={() => navigate("/tile/create", { state: { fromRecentTiles: true } })}
              className="px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all"
            >
              Create New Tile
            </Button>
          )}
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          className="h-[calc(100vh-12rem)] overflow-y-auto hide-scrollbar"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 pb-6">
            {tiles.map((tile) => (
              <div
                key={tile.tid}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group border border-gray-100 hover:border-gray-200"
              >
                {/* Image Section - Premium Card */}
                <div className="relative aspect-[3/2] overflow-hidden bg-[var(--surface-2)]">
                  <img
                    src={getImageUrl(tile)}
                    alt={tile.title}
                    className="w-full h-full object-fill transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>

                  {/* Status Badge */}
                  <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg backdrop-blur-sm ${getStatusColor(tile.status)}`}>
                    {tile.status || "Unpublished"}
                  </div>
                </div>

                {/* Info Section */}
                <div className="p-4 cw-surface">
                  {/* Title */}
                  <h3 className="text-gray-800 font-bold text-lg truncate mb-3" title={tile.title}>
                    {tile.title}
                  </h3>
                  <h5 className="text-gray-800 font-semibold text-md truncate mb-3">Source : {allSource[tile.source] || tile.source}</h5>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-5 gap-1.5">
                    <button
                      onClick={() => handlePreview(tile)}
                      className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-500 hover:text-white transition-all duration-300 group/btn"
                      title="Preview"
                    >
                      <FaEye size={16} />
                      <span className="text-[9px] font-bold uppercase tracking-wide">Preview</span>
                    </button>

                    <button
                      onClick={() => navigate(`/tile/${tile.tid}`, { state: { fromRecentTiles: true } })}
                      className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-500 hover:text-white transition-all duration-300 group/btn"
                      title="Edit"
                    >
                      <MdModeEditOutline size={16} />
                      <span className="text-[9px] font-bold uppercase tracking-wide">Edit</span>
                    </button>

                    <button
                      onClick={() => handleDuplicate(tile)}
                      className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-500 hover:text-white transition-all duration-300 group/btn"
                      title="Duplicate"
                    >
                      <IoDuplicateOutline size={16} />
                      <span className="text-[9px] font-bold uppercase tracking-wide">Copy</span>
                    </button>

                    <button
                      onClick={() => handleDelete(tile)}
                      className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 bg-red-50 text-red-600 rounded-xl hover:bg-red-500 hover:text-white transition-all duration-300 group/btn"
                      title="Delete"
                    >
                      <MdDelete size={16} />
                      <span className="text-[9px] font-bold uppercase tracking-wide">Delete</span>
                    </button>

                    <button
                      onClick={() => showUpdateInfo(tile)}
                      className="flex flex-col items-center justify-center gap-1 py-2.5 px-1 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-700 hover:text-white transition-all duration-300 group/btn"
                      title="Info"
                    >
                      <AiOutlineInfoCircle size={16} />
                      <span className="text-[9px] font-bold uppercase tracking-wide">Info</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Intersection observer target */}
          <div ref={loadMoreRef} className="h-4"></div>

          {loadingMore && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-200 border-t-blue-600"></div>
              <span className="ml-3 text-gray-500">Loading more tiles...</span>
            </div>
          )}

          {!hasMore && tiles.length > 0 && (
            <div className="text-center py-8 text-gray-400">
              <p>No more tiles to load</p>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTile && (
        <Modal
          customClasses="p-0 w-full max-w-5xl"
          showModal={showPreview}
          setShowModal={setShowPreview}
        >
          <MovieTile data={previewTile} />
        </Modal>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <Modal
          customClasses="max-w-md w-full"
          showModal={showFilterModal}
          setShowModal={setShowFilterModal}
        >
          <div className="p-6 overflow-hidden">
            <h2 className="text-xl font-semibold mb-4">Filter Tiles</h2>
            <div className="space-y-4">
              {tempFilters.length > 0 &&
                tempFilters.map((filter, index) => (
                  <div key={index} className="space-y-2">
                    <ReactSelect
                      className="w-full"
                      menuPortalTarget={document.body}
                      styles={buildSelectStyles(isDark)}
                      options={category.map((f) => ({
                        value: f.key,
                        label: f.label,
                      }))}
                      value={category.find((f) => f.key === filter.category)}
                      onChange={(selectedOption) =>
                        handleFilterChange(index, "category", selectedOption?.value)
                      }
                      placeholder="Select Field"
                      isSearchable
                    />
                    {filter.category && (
                     <AsyncPaginate
  isMulti
  key={filter.category} // important reset on category change
  value={filter.value.map((val) => ({
    value: val,
    label:
      filter.category === "languages"
        ? allLanguages[val] || val
        : filter.category === "source"
        ? allSource[val] || val
        : filter.category === "contentType"
        ? allContentTypes.find((c) => c.value === val)?.label || val
        : filter.category === "director"
        ? allDirectors[val] || val
        : filter.category === "cast"
        ? allCastMembers[val] || val
        : filter.category === "genre"
        ? allGenres[val] || val
        : filter.category === "year"
? String(val)
        : val,
  }))}

  loadOptions={(search, loadedOptions, additional) =>
    loadFilterOptions(
      filter.category,
      search,
      loadedOptions,
      additional || { page: 0 }
    )
  }

  additional={{ page: 0 }}

  onChange={(selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    handleFilterChange(index, "value", selectedValues);
  }}

  placeholder={`Select ${filter.category}`}
  isSearchable
  debounceTimeout={500}

  menuPortalTarget={document.body}
  styles={buildSelectStyles(isDark)}
/>
                    )}
                    <button
                      className="mt-2 hover:underline text-sm"
                      onClick={() => removeFilter(index)}
                    >
                      Remove Filter
                    </button>
                  </div>
                ))}
              <Button
                variant="primary"
                onClick={addFilter}
                className="mt-2 text-white hover:underline text-sm"
              >
                + Add Filter
              </Button>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setShowFilterModal(false)}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RecentTiles;